import { useState } from 'react';

import { useUpdateWebsiteLandingType } from './useUpdateWebsiteLandingType';
import type { LandingType } from '../../../hooks/usePlusWebsites';

interface UseSwitchLandingTypeOptions {
  websiteId: string;
  landingType: LandingType;
  externalHomepageUrl: string | null | undefined;
}

export interface SwitchLandingType {
  label: string;
  isSwitching: boolean;
  /** Switches straight back to the builder, or asks for the external URL first. */
  start: () => void;
  isUrlModalOpen: boolean;
  closeUrlModal: () => void;
  saveExternalUrl: (url: string) => void;
  externalHomepageUrl: string;
}

// Shared by the home page card's button and the home page row's overflow menu, so both offer the
// same behaviour and copy while owning their own trigger.
export function useSwitchLandingType({
  websiteId,
  landingType,
  externalHomepageUrl,
}: UseSwitchLandingTypeOptions): SwitchLandingType {
  const [isUrlModalOpen, setIsUrlModalOpen] = useState(false);
  const updateLandingType = useUpdateWebsiteLandingType();
  const isExternal = landingType === 'external';

  return {
    label: isExternal ? 'Switch to builder' : 'Switch to external',
    isSwitching: updateLandingType.isLoading,
    start: () => {
      if (isExternal) {
        updateLandingType.mutate({ websiteId, landingType: 'in-app' as const });
        return;
      }
      setIsUrlModalOpen(true);
    },
    isUrlModalOpen,
    closeUrlModal: () => setIsUrlModalOpen(false),
    saveExternalUrl: (url: string) =>
      updateLandingType.mutate(
        { websiteId, landingType: 'external' as const, externalHomepageUrl: url },
        { onSuccess: () => setIsUrlModalOpen(false) }
      ),
    externalHomepageUrl: externalHomepageUrl ?? '',
  };
}
